import { Button } from '@/components/shadcn/button'
import { Card, CardContent } from '@/components/shadcn/card'
import { DynamicPictogram } from '@/components/pictograms/DynamicPictogram'
import { presenceAnimation } from '@/components/ui/NoSeachResultsDisplay'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { ApplicationIcon, WorkspaceIcon } from '@/const'
import { cn } from '@/lib/utils'
import { ENDPOINTS } from '@/util/endpoints'
import { format } from 'date-fns'
import { AnimatePresence, motion } from 'motion/react'
import { forwardRef, useState, type ComponentPropsWithoutRef } from 'react'
import { Link } from 'react-router'
import {
  getProjectWorkspacePaths,
  getWorkspaceApprovalDate,
  getWorkspaceArchived,
  getWorkspaceHasAccess,
  ProjectWorkspace,
} from './projectWorkspaceUtils'
import { Badge } from '@/components/shadcn/badge'
import clsx from 'clsx'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { CreateWorkspaceForm } from '@/pages/Subscriptions/Forms/CreateWorkspaceForm'
import { RequestWorkspaceForm } from '@/pages/Subscriptions/Forms/RequestWorkspaceForm'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'

const AddWorkspaceGridButton = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<'button'> & { label: string }>(
  function AddWorkspaceGridButton({ label, className, type = 'button', ...props }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          'relative flex w-full min-w-0 cursor-pointer appearance-none flex-col items-center gap-2 border-b-4 border-r-4 border-background bg-background p-3 hover:bg-muted/30',
          className
        )}
        {...props}
      >
        <span aria-hidden className="pointer-events-none absolute inset-0 border-[3px] border-dashed border-border" />
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <IconPlusOutline className="size-16 text-border" />
        </span>
        <span className="size-28 shrink-0" aria-hidden />
        <span className="max-w-full truncate text-center text-sm text-muted-foreground">{label}</span>
      </button>
    )
  }
)

export function ProjectNewWorkspaceGridItem({
  organisationId,
  projectId,
  projectName,
  excludedAppIds = [],
  canAddWorkspace,
  canRequestWorkspace,
}: {
  organisationId: number
  projectId: number
  projectName: string
  excludedAppIds?: number[]
  canAddWorkspace: boolean
  canRequestWorkspace: boolean
}) {
  if (!canAddWorkspace || !canRequestWorkspace) {
    const Form = canAddWorkspace ? CreateWorkspaceForm : RequestWorkspaceForm
    const label = canAddWorkspace ? 'Add workspace' : 'Request workspace'

    return (
      <Form
        organisationId={organisationId}
        projectId={projectId}
        excludedAppIds={excludedAppIds}
        sheetTitle={`${projectName} > ${canAddWorkspace ? 'Create' : 'Request'} workspace`}
        sheetDescription={`${canAddWorkspace ? 'Create' : 'Request'} a new workspace in this project.`}
        sheetTrigger={<AddWorkspaceGridButton label={label} />}
      />
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <AddWorkspaceGridButton label="New workspace" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {canAddWorkspace && (
          <CreateWorkspaceForm
            organisationId={organisationId}
            projectId={projectId}
            excludedAppIds={excludedAppIds}
            sheetTitle={`${projectName} > Create workspace`}
            sheetDescription="Create a new workspace in this project."
            sheetTrigger={
              <DropdownMenuItem onSelect={(event) => event.preventDefault()}>Add a workspace</DropdownMenuItem>
            }
          />
        )}
        {canRequestWorkspace && (
          <RequestWorkspaceForm
            organisationId={organisationId}
            projectId={projectId}
            excludedAppIds={excludedAppIds}
            sheetTitle={`${projectName} > Request workspace`}
            sheetDescription="Request a new workspace in this project."
            sheetTrigger={
              <DropdownMenuItem onSelect={(event) => event.preventDefault()}>Request a workspace</DropdownMenuItem>
            }
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function ProjectLinkedWorkspaceGridItem({
  workspace,
  projectId,
  organisationId,
}: {
  workspace: ProjectWorkspace
  projectId: number
  organisationId: number
}) {
  const { application } = workspace
  const { launchAppPath, workspaceDetailsPath } = getProjectWorkspacePaths(workspace, projectId, organisationId)
  const hasAccess = getWorkspaceHasAccess(workspace)
  const archived = getWorkspaceArchived(workspace)
  const isDimmed = archived || !hasAccess
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'relative flex min-w-0 flex-col items-center gap-2 border-b-4 border-r-4 border-background bg-offwhite p-3',
        isDimmed && 'opacity-50'
      )}
    >
      <DynamicPictogram
        gradient={!isDimmed}
        pictogramName={workspace.pictureUrl ?? 'PictogramCamera'}
        className={clsx('size-28', isDimmed && 'opacity-50')}
      />
      <TooltipButton
        side="left"
        tooltip={
          <div className="flex items-center gap-5 p-3">
            <Badge variant="outline" className="text-background opacity-70">
              {workspace.appKey}
            </Badge>
            <span>{`${application}`}</span>
          </div>
        }
        className="block min-w-0"
      >
        <span className="w-auto max-w-full truncate text-center text-sm text-foreground">{application}</span>
      </TooltipButton>

      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 top-0 flex h-[50%] w-full items-center justify-evenly gap-2 bg-foreground/5 p-2 backdrop-blur"
          >
            {archived ? (
              <span className="text-base font-bold text-muted-foreground">Archived</span>
            ) : !hasAccess ? (
              <>
                <span className="text-base font-bold text-muted-foreground">No access</span>
              </>
            ) : (
              <>
                <TooltipButton tooltip="Launch Application" side="top">
                  <Button size="icon" className="size-12" asChild>
                    <Link to={launchAppPath} aria-label="Launch Application">
                      <ApplicationIcon className="size-8" />
                    </Link>
                  </Button>
                </TooltipButton>
                <TooltipButton tooltip="Open Workspace" side="top">
                  <Button variant="outline" size="icon" className="size-12" asChild>
                    <Link to={workspaceDetailsPath} aria-label="Open Workspace">
                      <WorkspaceIcon className="size-8" />
                    </Link>
                  </Button>
                </TooltipButton>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export function ProjectLinkedWorkspaceRow({
  workspace,
  projectId,
  organisationId,
}: {
  workspace: ProjectWorkspace
  projectId: number
  organisationId: number
}) {
  const { application, image } = workspace
  const { launchAppPath } = getProjectWorkspacePaths(workspace, projectId, organisationId)
  const hasAccess = getWorkspaceHasAccess(workspace)
  const archived = getWorkspaceArchived(workspace)
  const approvalDate = getWorkspaceApprovalDate(workspace)
  const isDimmed = archived || !hasAccess

  const imageStyle = image
    ? { backgroundImage: `url(${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=${image})` }
    : undefined

  const rightLabel = hasAccess && approvalDate ? format(new Date(approvalDate), 'dd MMM yyyy') : 'Access review due'

  return (
    <Link
      to={launchAppPath}
      className={cn(
        'flex items-center gap-3 border-b border-border py-3 last:border-b-0 hover:bg-muted/30',
        isDimmed && 'opacity-50'
      )}
    >
      {image ? (
        <div className="size-8 shrink-0 rounded bg-muted bg-cover bg-center" style={imageStyle} />
      ) : (
        <ApplicationIcon className="size-4 shrink-0 text-muted-foreground" />
      )}
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{application}</span>
      <span className="shrink-0 text-sm text-muted-foreground">{rightLabel}</span>
    </Link>
  )
}

export function ProjectWorkspacesGridItem({
  workspace,
  projectId,
  organisationId,
  className,
  launchOnly = false,
}: {
  workspace: ProjectWorkspace
  projectId: number
  organisationId: number
  className?: string
  launchOnly?: boolean
}) {
  const { workspaceId, application, image } = workspace
  const { launchAppPath, workspaceDetailsPath } = getProjectWorkspacePaths(workspace, projectId, organisationId)

  const imageStyle = image
    ? { backgroundImage: `url(${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=${image})` }
    : undefined

  if (launchOnly) {
    const card = (
      <Card className="h-full gap-0 overflow-hidden bg-background py-0 shadow ring-0 transition-all hover:bg-secondary dark:hover:border-secondary">
        <div className="grid h-full grid-cols-[100px_1fr]">
          <div className="min-h-[44px] bg-muted bg-cover bg-center" style={imageStyle} />
          <CardContent className="flex items-center bg-offwhite py-4 hover:bg-secondary">
            <p className="mt-1 min-w-0 truncate text-base font-medium">{application}</p>
          </CardContent>
        </div>
      </Card>
    )

    return (
      <motion.div
        className={cn('relative overflow-visible', className)}
        layout
        key={workspaceId}
        {...presenceAnimation}
      >
        <Link to={launchAppPath} className="block h-full">
          {card}
        </Link>
      </motion.div>
    )
  }

  return (
    <motion.div className={cn('relative overflow-visible', className)} layout key={workspaceId} {...presenceAnimation}>
      <div className="overflow-hidden rounded-lg border border-border bg-offwhite shadow-sm">
        <div className="flex items-center gap-4 px-5 py-4">
          <div className="size-12 shrink-0 rounded-md bg-muted bg-cover bg-center" style={imageStyle} />
          <p className="min-w-0 flex-1 truncate text-base font-bold text-foreground">{application}</p>
          <div className="flex shrink-0 items-center gap-2">
            <TooltipButton tooltip="Launch Application">
              <Button variant="ghost" size="icon" asChild>
                <Link to={launchAppPath} aria-label="Launch Application">
                  <ApplicationIcon className="size-6" />
                </Link>
              </Button>
            </TooltipButton>
            <TooltipButton tooltip="Open Workspace">
              <Button variant="ghost" size="icon" asChild>
                <Link to={workspaceDetailsPath} aria-label="Open Workspace">
                  <WorkspaceIcon className="size-6" />
                </Link>
              </Button>
            </TooltipButton>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
